/**
 * Three-way Ops Center view toggle (PRD §5.8 S-03).
 *
 * Buttons: [ Map ] [ Schematic ] [ Waterfall ]
 *
 * Persists the selection in localStorage["sentinel_ops_view"] so a
 * reload returns the operator to their last view.
 *
 * Keyboard shortcuts (1, 2, 3) are wired by the parent OpsCenter via
 * the exported `OPS_VIEW_KEYS` map — this component intentionally
 * doesn't manage global keyboard state, only renders + reads the
 * current value.
 */

import { Map as MapIcon, Activity, Layers } from "lucide-react";

export type OpsView = "map" | "schematic" | "waterfall";
export const OPS_VIEW_STORAGE_KEY = "sentinel_ops_view";

/** Mapping used by both the toggle UI and the keyboard shortcut handler. */
export const OPS_VIEW_KEYS: { key: string; view: OpsView; label: string }[] = [
  { key: "1", view: "map", label: "Map" },
  { key: "2", view: "schematic", label: "Schematic" },
  { key: "3", view: "waterfall", label: "Waterfall" },
];

const ICONS: Record<OpsView, typeof MapIcon> = {
  map: MapIcon,
  schematic: Layers,
  waterfall: Activity,
};

export function loadStoredOpsView(): OpsView {
  try {
    const v = localStorage.getItem(OPS_VIEW_STORAGE_KEY);
    if (v === "map" || v === "schematic" || v === "waterfall") return v;
  } catch {
    /* SSR or storage disabled */
  }
  return "map";
}

export function persistOpsView(view: OpsView): void {
  try {
    localStorage.setItem(OPS_VIEW_STORAGE_KEY, view);
  } catch {
    /* ignore — feature degrades gracefully */
  }
}

interface OpsViewToggleProps {
  value: OpsView;
  onChange: (view: OpsView) => void;
}

export function OpsViewToggle({ value, onChange }: OpsViewToggleProps) {
  return (
    <div
      role="tablist"
      aria-label="Ops Center view"
      className="inline-flex rounded-md border border-slate-700 overflow-hidden text-xs"
      data-testid="ops-view-toggle"
    >
      {OPS_VIEW_KEYS.map(({ view, label, key }) => {
        const active = value === view;
        const Icon = ICONS[view];
        return (
          <button
            key={view}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(view)}
            data-testid={`ops-view-${view}`}
            title={`Press ${key}`}
            className={
              `inline-flex items-center gap-1.5 px-3 py-1.5 transition ` +
              (active
                ? "bg-teal-500/20 text-teal-200"
                : "bg-slate-900/40 text-slate-400 hover:bg-slate-800/60")
            }
          >
            <Icon className="size-3.5" />
            <span>{label}</span>
            <span className="ml-1 text-[10px] text-slate-500 font-mono">
              {key}
            </span>
          </button>
        );
      })}
    </div>
  );
}
